
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import './App.css';
import logo from './assets/logo.png';
import { auth } from './firebase';
import { onAuthStateChanged, User } from 'firebase/auth';

import SocialLinks from './components/SocialLinks';
import Home from './pages/Home';
import CalculatorsPage from './pages/CalculatorsPage';
import InvoiceGeneratorPage from './pages/InvoiceGeneratorPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import ContactPage from './pages/ContactPage';
import GlossaryPage from './pages/GlossaryPage';
import DashboardPage from './pages/DashboardPage';
import AuthForm from './components/AuthForm';
import CashbookPage from './pages/CashbookPage';
import TranslatorPage from './pages/TranslatorPage';
import TaxToolsPage from './pages/TaxToolsPage';
import NewsPage from './pages/NewsPage';
import MarketPricePage from './pages/MarketPricePage';

function App() {
  const [expanded, setExpanded] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoadingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  const handleNavClose = () => setExpanded(false);

  if (loadingAuth) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        Loading authentication...
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <SocialLinks />
        <Navbar bg="dark" variant="dark" expand="lg" sticky="top" expanded={expanded} onToggle={setExpanded}>
          <Container>
            <Navbar.Brand as={Link} to="/">
              <img
                src={logo}
                width="30"
                height="30"
                className="d-inline-block align-top"
                alt="Cashvax.in logo"
              />{' '}
              Cashvax.in
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="ms-auto">
                <Nav.Link as={Link} to="/" onClick={handleNavClose}>Home</Nav.Link>
                <Nav.Link as={Link} to="/contact" onClick={handleNavClose}>Contact</Nav.Link>

                <NavDropdown title="Calculators" id="calculators-nav-dropdown">
                  <NavDropdown.Item as={Link} to="/calculators" onClick={handleNavClose}>All Calculators</NavDropdown.Item>
                  {/* Add more specific calculator links here if needed */}
                </NavDropdown>

                <NavDropdown title="Accounting" id="accounting-nav-dropdown">
                  <NavDropdown.Item as={Link} to="/invoice" onClick={handleNavClose}>Invoice Generator</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/cashbook" onClick={handleNavClose}>Cashbook</NavDropdown.Item>
                </NavDropdown>

                <NavDropdown title="Financial" id="financial-nav-dropdown">
                  <NavDropdown.Item as={Link} to="/glossary" onClick={handleNavClose}>Glossary</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/translator" onClick={handleNavClose}>Translator</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/blog" onClick={handleNavClose}>Blog</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/news" onClick={handleNavClose}>News & Updates</NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/market-price" onClick={handleNavClose}>Live Market Price</NavDropdown.Item>
                </NavDropdown>
                {currentUser ? (
                  <>
                    <Nav.Link as={Link} to="/dashboard" onClick={handleNavClose}>Dashboard</Nav.Link>
                    <Nav.Link onClick={() => auth.signOut()}>Logout</Nav.Link>
                  </>
                ) : (
                  <Nav.Link as={Link} to="/login" onClick={handleNavClose}>Login</Nav.Link>
                )}
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>

        <Routes>
          <Route path="/" element={<Home currentUser={currentUser} />} />
          <Route path="/calculators" element={<CalculatorsPage currentUser={currentUser} />} />
          <Route path="/invoice" element={<InvoiceGeneratorPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogPostPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/glossary" element={<GlossaryPage />} />
          <Route path="/login" element={<AuthForm onAuthSuccess={() => setExpanded(false)} />} />
          <Route path="/dashboard" element={<DashboardPage currentUser={currentUser} />} />
          <Route path="/cashbook" element={<CashbookPage />} />
          <Route path="/translator" element={<TranslatorPage />} />
          <Route path="/tax-tools" element={<TaxToolsPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/market-price" element={<MarketPricePage />} />
        </Routes>

        <footer className="bg-dark text-white text-center py-3">
          <Container>
            <p>&copy; 2025 Cashvax.in. All Rights Reserved.</p>
          </Container>
        </footer>
      </div>
    </Router>
  );
}

export default App;
